import { useEffect, useRef } from 'react'
import { AlertTriangle, Info, X } from 'lucide-react'

interface ModalProps {
  open: boolean
  title: string
  message: string
  variant?: 'info' | 'danger'
  confirmLabel?: string
  cancelLabel?: string
  onConfirm: () => void
  onCancel?: () => void
}

export default function Modal({ open, title, message, variant = 'info', confirmLabel = 'OK', cancelLabel, onConfirm, onCancel }: ModalProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (open) confirmRef.current?.focus()
  }, [open])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return
      if (e.key === 'Escape' && onCancel) onCancel()
      if (e.key === 'Enter') onConfirm()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onConfirm, onCancel])

  if (!open) return null

  const isDanger = variant === 'danger'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel ?? onConfirm} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDanger ? 'bg-red-100' : 'bg-blue-50'}`}>
              {isDanger
                ? <AlertTriangle className="w-5 h-5 text-red-500" />
                : <Info className="w-5 h-5 text-danone-blue" />}
            </div>
            <h2 className="text-base font-bold text-slate-800">{title}</h2>
          </div>
          {onCancel && (
            <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <p className="text-sm text-slate-600 leading-relaxed">{message}</p>

        <div className="flex gap-2 justify-end">
          {cancelLabel && onCancel && (
            <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors ${isDanger ? 'bg-red-500 hover:bg-red-600' : 'bg-danone-blue hover:bg-blue-800'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
